<template>
  <div>
    <v-simple-table dense class="summary-table">
      <thead>
        <tr>
          <th class="col-label"></th>
          <th class="col-num text-right">Cant.</th>
          <th class="col-amount text-right">Monto neto</th>
          <th class="col-bar text-center">Incid.</th>
        </tr>
      </thead>
      <tbody>
        <!-- Total -->
        <tr class="row-total">
          <td>Total presentado</td>
          <td class="text-right">{{ summary?.total_presented_count || 0 }}</td>
          <td class="text-right">{{ prependToValue }}{{ summary?.total_presented | toThousandFilter(decimalPlaces) }}</td>
          <td class="cell-bar">
            <incidence-bar :value="100" :max="maxIncidence" />
          </td>
        </tr>

        <!-- Aumentos -->
        <tr class="row-subtotal row-section-break">
          <td colspan="4">
            Aumentos <span class="badge badge-inc">+</span>
          </td>
        </tr>
        <tr>
          <td class="indent">Presentados</td>
          <td class="text-right">{{ summary?.increases_presented_count || 0 }}</td>
          <td class="text-right">{{ prependToValue }}{{ summary?.increases_presented | toThousandFilter(decimalPlaces) }}</td>
          <td class="cell-bar">
            <incidence-bar :value="incidenceVal(summary?.increases_presented_count)" :max="maxIncidence" />
          </td>
        </tr>
        <tr v-for="row in increaseRows" :key="'inc-' + row.label" :class="{ 'row-approved': row.isApproved }">
          <td class="indent">{{ row.label }}</td>
          <td class="text-right">{{ row.count }}</td>
          <td class="text-right">{{ prependToValue }}{{ row.amount | toThousandFilter(decimalPlaces) }}</td>
          <td class="cell-bar">
            <incidence-bar :value="row.incidence * 100" :max="maxIncidence" />
          </td>
        </tr>

        <!-- Disminuciones -->
        <tr class="row-subtotal row-section-break">
          <td colspan="4">
            Disminuciones <span class="badge badge-dec">−</span>
          </td>
        </tr>
        <tr>
          <td class="indent">Presentadas</td>
          <td class="text-right">{{ summary?.decreases_presented_count || 0 }}</td>
          <td class="text-right amount-negative">{{ prependToValue }}{{ summary?.decreases_presented | toThousandFilter(decimalPlaces) }}</td>
          <td class="cell-bar">
            <incidence-bar :value="-incidenceVal(summary?.decreases_presented_count)" :max="maxIncidence" />
          </td>
        </tr>
        <tr v-for="row in decreaseRows" :key="'dec-' + row.label" :class="{ 'row-approved': row.isApproved }">
          <td class="indent">{{ row.label }}</td>
          <td class="text-right">{{ row.count }}</td>
          <td class="text-right amount-negative">{{ prependToValue }}{{ row.amount | toThousandFilter(decimalPlaces) }}</td>
          <td class="cell-bar">
            <incidence-bar :value="-(row.incidence * 100)" :max="maxIncidence" />
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <div v-if="loading" class="text-center my-2">
      <v-progress-linear indeterminate color="#2D4B95"></v-progress-linear>
    </div>

    <div v-if="!loading && !summary" class="text-center my-2">
      <p class="no-data">Sin datos</p>
    </div>
  </div>
</template>

<script>
import { getAdditionalsSimpleSummary } from '@/helpers/api/additionals'
import { HorizontalBar } from 'vue-chartjs'

const STATUS_LABELS = {
  in_review: 'En revisión',
  agreed:    'Consensuados',
  approved:  'Aprobados',
  cancelled: 'Nulos'
}

const STATUS_LABELS_F = {
  in_review: 'En revisión',
  agreed:    'Consensuadas',
  approved:  'Aprobadas',
  cancelled: 'Nulas'
}

const COLOR_POS = 'rgba(99, 153, 34, 0.75)'
const COLOR_NEG = 'rgba(211, 47, 47, 0.75)'

// Plugin que dibuja la línea central y el label % encima de la barra
const centerLinePlugin = {
  beforeDraw(chart) {
    // Fondo transparente — elimina el fondo blanco por defecto
    const ctx = chart.ctx
    ctx.save()
    ctx.clearRect(0, 0, chart.width, chart.height)
    ctx.restore()
  },
  afterDraw(chart) {
    const ctx = chart.ctx
    const xAxis = chart.scales['x-axis-0']
    const yAxis = chart.scales['y-axis-0']
    if (!xAxis || !yAxis) return

    const zeroX  = xAxis.getPixelForValue(0)
    const top    = yAxis.top
    const bottom = yAxis.bottom

    // Línea vertical en 0
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(zeroX, top)
    ctx.lineTo(zeroX, bottom)
    ctx.strokeStyle = 'rgba(0,0,0,0.3)'
    ctx.lineWidth = 1
    ctx.stroke()
    ctx.restore()

    // Label % — dentro si cabe, fuera si la barra es muy corta
    const dataset = chart.data.datasets[0]
    const meta    = chart.getDatasetMeta(0)
    if (!meta.data.length) return

    const bar    = meta.data[0]
    const value  = dataset.data[0]
    const label  = (value < 0 ? '-' : '') + Math.abs(value).toFixed(1) + '%'

    const barCenterY = bar._model.y
    // Calcular el extremo real de la barra según el valor (no _model.x que siempre es positivo)
    const barTip     = xAxis.getPixelForValue(value)
    const barLength  = Math.abs(barTip - zeroX)

    ctx.save()
    ctx.font = 'bold 9px sans-serif'

    const textWidth   = ctx.measureText(label).width
    const minBarWidth = textWidth + 8

    let labelX, align, color

    if (barLength >= minBarWidth) {
      // Cabe dentro: centrado entre el cero y el extremo real
      labelX = (zeroX + barTip) / 2
      align  = 'center'
      color  = 'rgba(0,0,0,0.8)'
    } else {
      // No cabe: fuera de la barra, alejándose del cero
      const offset = 4
      if (value >= 0) {
        labelX = barTip + offset
        align  = 'left'
      } else {
        labelX = barTip - offset
        align  = 'right'
      }
      color = 'rgba(0,0,0,0.6)'
    }

    ctx.fillStyle = color
    ctx.textAlign = align
    ctx.textBaseline = 'middle'
    ctx.fillText(label, labelX, barCenterY)
    ctx.restore()
  }
}

const IncidenceBar = {
  name: 'IncidenceBar',
  extends: HorizontalBar,
  props: {
    value: { type: Number, default: 0 },
    max:   { type: Number, default: 100 }
  },
  computed: {
    chartData() {
      return {
        labels: [''],
        datasets: [{
          data: [this.value],
          backgroundColor: this.value >= 0 ? COLOR_POS : COLOR_NEG,
          borderWidth: 0,
          barThickness: 15
        }]
      }
    },
    chartOptions() {
      const absMax = this.max || 100
      return {
        responsive: true,
        backgroundColor: 'transparent',
        maintainAspectRatio: false,
        animation: { duration: 0 },
        legend: { display: false },
        tooltips: { enabled: false },
        layout: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
        scales: {
          xAxes: [{
            display: false,
            ticks: { min: -absMax, max: absMax }
          }],
          yAxes: [{
            display: false,
            gridLines: { display: false }
          }]
        }
      }
    }
  },
  mounted() {
    this.addPlugin(centerLinePlugin)
    this.renderChart(this.chartData, this.chartOptions)
  },
  watch: {
    value() { this.renderChart(this.chartData, this.chartOptions) },
    max()   { this.renderChart(this.chartData, this.chartOptions) }
  }
}

export default {
  name: 'AdditionalsSimpleSummary',
  components: { IncidenceBar },
  props: {
    currencyId: { type: Number, default: 2 }
  },
  data() {
    return {
      loading: false,
      summary: null,
      currencyItems: [
        { id: 1, label: 'USD' },
        { id: 2, label: 'CLP' },
        { id: 3, label: 'UF' }
      ]
    }
  },
  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    activeProjectId() {
      return this.projectId || this.selectedProject?.id
    },
    prependToValue() {
      const code   = this.summary?.currency_info?.code   || 'CLP'
      const symbol = this.summary?.currency_info?.symbol || '$'
      if (code !== symbol) return `${code} ${symbol}`
      return code
    },
    decimalPlaces() {
      return this.summary?.currency_info?.code === 'UF' ? 2 : 0
    },
    increaseRows() {
      return this.makeRows(this.summary?.increases || {}, STATUS_LABELS)
    },
    decreaseRows() {
      return this.makeRows(this.summary?.decreases || {}, STATUS_LABELS_F)
    },
    maxIncidence() {
      if (!this.summary) return 100
      const allPcts = [
        ...this.increaseRows.map(r => Math.abs(r.incidence * 100)),
        ...this.decreaseRows.map(r => Math.abs(r.incidence * 100)),
        this.incidenceVal(this.summary.increases_presented_count),
        this.incidenceVal(this.summary.decreases_presented_count),
        100
      ]
      return Math.ceil(Math.max(...allPcts) / 10) * 10
    }
  },
  mounted() {
    this.fetchSummary()
  },
  methods: {
    makeRows(obj, labels) {
      return Object.entries(labels).map(([key, label]) => ({
        label,
        count:      obj[key]?.count  || 0,
        amount:     obj[key]?.amount || 0,
        incidence:  (obj[key]?.amount / this.summary?.total_presented) || 0,
        isApproved: key === 'approved'
      }))
    },
    incidenceVal(count) {
      if (!this.summary?.total_presented_count) return 0
      return (count / this.summary.total_presented_count) * 100
    },
    async fetchSummary() {
      if (!this.selectedProject?.id) return
      this.loading = true
      this.summary = null
      try {
        const params = { project_id: this.selectedProject.id, currency_id: this.currencyId }
        const res = await getAdditionalsSimpleSummary(params)
        if (res && res.code === 200) {
          this.summary = res
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.summary-table {
  font-size: 11px;
}

.col-label  { width: auto; }
.col-num    { width: 48px; }
.col-amount { width: 140px; }
.col-bar    { width: 80px; max-width: 80px; }

.summary-table ::v-deep thead th {
  font-size: 10px !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0,0,0,0.45);
  padding: 4px 8px !important;
  border-bottom: 1px solid rgba(0,0,0,0.15) !important;
  font-weight: 400;
}

.summary-table ::v-deep td {
  font-size: 11px !important;
  padding: 3px 8px !important;
  border-bottom: 1px solid rgba(0,0,0,0.06) !important;
  height: auto !important;
}

.cell-bar {
  padding: 2px 4px !important;
  vertical-align: middle;
	max-width: 100px !important;
	width: 100px !important;
}

.cell-bar ::v-deep canvas {
  max-height: 15px !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.row-total ::v-deep td {
  font-weight: 500;
  background: rgba(0,0,0,0.03);
}

.row-subtotal ::v-deep td {
  font-size: 10px !important;
  font-weight: 500;
  color: rgba(0,0,0,0.5);
  padding: 5px 8px !important;
  background: rgba(0,0,0,0.04);
}

.row-section-break ::v-deep td {
  border-top: 1.5px solid rgba(0,0,0,0.2) !important;
}

.indent {
  padding-left: 16px !important;
  color: rgba(0,0,0,0.6);
}

.row-approved ::v-deep td {
  background: #EAF3DE !important;
  color: #27500A !important;
  font-weight: 500;
}
.row-approved .indent {
  color: #3B6D11 !important;
}

.badge {
  display: inline-block;
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: 500;
  vertical-align: middle;
  margin-left: 4px;
}
.badge-inc { background: #EAF3DE; color: #3B6D11; }
.badge-dec { background: #FCEBEB; color: #D32F2F; }

.amount-negative {
  color: #D32F2F !important;
}
.row-approved .amount-negative {
  color: #B71C1C !important;
}

.no-data {
  font-size: 11px;
  color: rgba(0,0,0,0.4);
}
</style>